import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import type { ConsultaSubmission, InscricoesContent } from '../../services/api'
import { defaultMensagemEncerramento } from '../Home'
import { ChevronLeft, Download, Eye, Lock, LockOpen, X } from 'lucide-react'
import { toast } from 'sonner'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR')
}

export default function ConsultaManager() {
  const { token } = useAuth()
  const [submissions, setSubmissions] = useState<ConsultaSubmission[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [selected, setSelected] = useState<ConsultaSubmission | null>(null)

  const [inscricoes, setInscricoes] = useState<InscricoesContent>({ aberta: true, mensagem: '' })
  const [isSavingInscricoes, setIsSavingInscricoes] = useState(false)

  useEffect(() => {
    loadSubmissions()
  }, [token, page])

  useEffect(() => {
    api.getContent().then((content) => {
      if (content.inscricoes) setInscricoes(content.inscricoes)
    })
  }, [])

  const saveInscricoes = async (value: InscricoesContent) => {
    if (!token) return
    setIsSavingInscricoes(true)
    try {
      await api.updateContent(token, 'inscricoes', value)
      setInscricoes(value)
      toast.success(value.aberta ? 'Inscrições abertas' : 'Inscrições encerradas')
    } catch (error) {
      toast.error('Erro ao atualizar inscrições')
    } finally {
      setIsSavingInscricoes(false)
    }
  }

  const handleToggleInscricoes = () => {
    if (inscricoes.aberta && !window.confirm('Encerrar as inscrições? O formulário público deixará de aceitar novas contribuições.')) return
    saveInscricoes({ ...inscricoes, aberta: !inscricoes.aberta })
  }

  const loadSubmissions = async () => {
    if (!token) return
    setIsLoading(true)
    try {
      const data = await api.getAdminConsultaSubmissions(token, page)
      setSubmissions(data.submissions)
      setTotal(data.total)
    } catch (error) {
      toast.error('Erro ao carregar contribuições')
    } finally {
      setIsLoading(false)
    }
  }

  const handleExport = async () => {
    if (!token) return
    setIsExporting(true)
    try {
      await api.exportConsultaCsv(token)
    } catch (error) {
      toast.error('Erro ao exportar CSV')
    } finally {
      setIsExporting(false)
    }
  }

  const totalPages = Math.ceil(total / 50)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">Carregando...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div>
              <h1 className="text-xl font-serif font-bold text-[#0d2d47]">Consulta Pública</h1>
              <p className="text-sm text-gray-500">{total} contribuição(ões) recebida(s)</p>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={isExporting || total === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#0d6b3d] text-white rounded-lg hover:bg-[#0b5b35] transition-colors text-sm font-medium disabled:opacity-50"
          >
            <Download size={16} />
            {isExporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-[#0d2d47]">Inscrições</h2>
              <p className="text-sm text-gray-500">
                {inscricoes.aberta
                  ? 'Abertas — o formulário público está aceitando contribuições.'
                  : 'Encerradas — o formulário público não aceita novas contribuições.'}
              </p>
            </div>
            <button
              onClick={handleToggleInscricoes}
              disabled={isSavingInscricoes}
              className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 ${
                inscricoes.aberta ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0d6b3d] hover:bg-[#0b5b35]'
              }`}
            >
              {inscricoes.aberta ? <Lock size={16} /> : <LockOpen size={16} />}
              {inscricoes.aberta ? 'Encerrar inscrições' : 'Reabrir inscrições'}
            </button>
          </div>
          <label className="block mt-4 text-sm font-medium text-gray-700">
            Mensagem exibida quando encerradas
            <textarea
              value={inscricoes.mensagem}
              onChange={(e) => setInscricoes({ ...inscricoes, mensagem: e.target.value })}
              rows={2}
              placeholder={defaultMensagemEncerramento}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-normal"
            />
          </label>
          <button
            onClick={() => saveInscricoes(inscricoes)}
            disabled={isSavingInscricoes}
            className="mt-2 text-sm font-medium text-[#0d6b3d] hover:underline disabled:opacity-50"
          >
            Salvar mensagem
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {submissions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhuma contribuição recebida ainda.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Data</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Desafio</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Contribuição</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {submissions.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDate(s.createdAt)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{s.tipo}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{s.nome || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">{s.desafio || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{s.contribuicao}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => setSelected(s)}
                          className="p-1.5 text-gray-400 hover:text-[#0d6b3d] hover:bg-[#0d6b3d]/10 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">Página {page} de {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-sm text-gray-600 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-[#0d2d47]">Detalhes da Contribuição</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <DetailRow label="Data" value={formatDate(selected.createdAt)} />
              <DetailRow label="Tipo de participante" value={selected.tipo} />
              <DetailRow label="Nome" value={selected.nome} />
              <DetailRow label="E-mail" value={selected.email} />
              <DetailRow label="Telefone" value={selected.telefone} />
              <DetailRow label="CPF/CNPJ" value={selected.cpfCnpj} />
              <DetailRow label="Organização" value={selected.organizacao} />
              <DetailRow label="Perfil" value={selected.perfil} />
              <DetailRow label="Desafio relacionado" value={selected.desafio} />
              <DetailRow label="Contribuição" value={selected.contribuicao} block />
              <DetailRow label="Justificativa" value={selected.justificativa} block />
              <DetailRow label="TRL" value={selected.trl} />
              <DetailRow label="Descrição do TRL" value={selected.trlDescricao} block />
              <DetailRow label="Custos" value={selected.custos} block />
              <DetailRow label="Riscos e requisitos" value={selected.riscosRequisitos} block />
              <DetailRow label="Melhoria proposta ao desafio" value={selected.melhoriaDesafio} block />
              <DetailRow label="Comentários extras" value={selected.comentariosExtras} block />
              <DetailRow label="IP" value={selected.ipAddress} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value, block }: { label: string; value?: string | null; block?: boolean }) {
  if (!value) return null
  return (
    <div className={block ? '' : 'flex gap-2'}>
      <span className="font-medium text-gray-500 shrink-0">{label}:</span>
      <span className={`text-gray-800 ${block ? 'block mt-0.5' : ''}`}>{value}</span>
    </div>
  )
}
